// Wire + domain shapes for the new backend-driven Discover "Companies" mode.
// These map onto the DISCOVER_ENABLED-gated backend surface (feat/discover-
// backend-foundation):
//   GET    /api/discover/companies?view=all|preferred
//   GET    /api/discover/companies/:key/people
//   GET    /api/discover/preferred-companies
//   POST   /api/discover/preferred-companies      { name }
//   DELETE /api/discover/preferred-companies/:id
//
// The exact JSON envelopes returned by the foundation branch aren't pinned
// down yet, so realDiscoverApi normalizes each response into these clean
// domain types and the rest of the app only ever sees the shapes below. When
// DISCOVER_ENABLED=false the endpoints answer 503; that is surfaced as a
// distinct "disabled" UI state rather than an error (see isDiscoverDisabledError
// in hooks/useDiscoverCompanies.ts).

export type DiscoverCompanyView = "all" | "preferred";

// Backend sort modes for the companies list: by contact count (default) or by
// display name (A-Z). Mirrors the `sort` query param on GET /companies.
export type DiscoverCompanySort = "contacts" | "name";

// Params for the companies list. `search` is a case-insensitive substring match
// on the company name; both `search` and `sort` are applied server-side.
export interface DiscoverCompaniesParams {
  view: DiscoverCompanyView;
  search?: string;
  sort?: DiscoverCompanySort;
}

// One row in the companies list. `key` is the opaque identifier the backend
// expects back on the /:key/people route (may be a slug/normalized name).
export interface DiscoverCompany {
  key: string;
  name: string;
  contactCount: number;
  preferred: boolean;
}

// A person the user knows at a company. Everything past name is optional and
// only rendered when the backend has clean data for it.
export interface DiscoverPerson {
  id: string;
  name: string;
  title: string | null;
  company: string | null;
  /** 0-100 scale, matches Contact.relationshipStrength. */
  relationshipStrength: number;
  linkedInUrl: string | null;
  email?: string | null;
  lastActivityAt?: string | null;
  /** ContactSourceKind values for Network-style provenance icons. */
  sources?: string[];
}

// A persisted preferred-company row. `/companies` returns a `preferred`
// boolean but not this id, so the UI joins on `normalizedName`/`key` to find
// the id needed for DELETE.
export interface DiscoverPreferredCompany {
  id: string;
  name: string;
  normalizedName: string;
}

// Outreach-draft channel. Maps to the `channel` field on
// POST /companies/:key/outreach-draft; "both" asks for email + LinkedIn.
export type DiscoverOutreachChannel = "email" | "linkedin" | "both";

// Draft request. `contactId` is only sent for a person-specific draft and must
// be a real backend id - never the synthetic `discover-person-<index>` fallback
// (see hasRealDiscoverContactId). `intent` is free text (capped in the UI).
export interface DiscoverOutreachInput {
  contactId?: string;
  intent?: string;
  channel?: DiscoverOutreachChannel;
}

// Generated draft. Never sent, drafted to Gmail, or persisted - the UI only
// displays it and offers copy-to-clipboard.
export interface DiscoverOutreachDraft {
  email: { subject: string; body: string };
  linkedInDm: string;
  contextSummary: string;
}

export interface DiscoverApi {
  listCompanies(params: DiscoverCompaniesParams): Promise<DiscoverCompany[]>;
  listPeople(companyKey: string): Promise<DiscoverPerson[]>;
  listPreferredCompanies(): Promise<DiscoverPreferredCompany[]>;
  addPreferredCompany(name: string): Promise<DiscoverPreferredCompany>;
  removePreferredCompany(id: string): Promise<void>;
  createOutreachDraft(
    companyKey: string,
    input: DiscoverOutreachInput,
  ): Promise<DiscoverOutreachDraft>;
}

// Shared normalizer so the companies list and the preferred list join on the
// same key (trim + lowercase). Kept here so both the API adapter and the hook
// agree on the mapping.
export function normalizeCompanyName(value: string): string {
  return value.trim().toLowerCase();
}

// Prefix realDiscoverApi assigns to a person when the payload has no id/
// contactId. Such ids are display-only and must NOT be sent back as a
// `contactId` - the person-level Draft button is hidden for them.
export const SYNTHETIC_DISCOVER_PERSON_ID_PREFIX = "discover-person-";

export function hasRealDiscoverContactId(person: Pick<DiscoverPerson, "id">): boolean {
  return (
    person.id.trim().length > 0 &&
    !person.id.startsWith(SYNTHETIC_DISCOVER_PERSON_ID_PREFIX)
  );
}
